import { useEffect } from 'react'
import { useIsAnyActionBlocked } from '@editorHooks'

/**
 * Prompts the browser's native "leave site?" dialog on reload/close/navigation-away while any
 * block action is registered (an unsaved form — Bemessung, Zugverbindertabelle, …).
 * Renders nothing.
 *
 * The listener is registered once and reads the latest blocked state imperatively, so it is not
 * re-attached on every dirty/clean transition.
 */
const UnsavedActionsReloadGuard = (): null => {
  const { isBlockedFn: isAnyActionBlockedFn } = useIsAnyActionBlocked()

  useEffect(() => {
    // Never register the guard under Cypress: the runner cannot dismiss the native beforeunload
    // dialog, and while it is open the browser suspends all JS — including Cypress itself — so the
    // next cy.visit after any form is left dirty freezes the run until the CI job hits its timeout.
    if (window.Cypress) return

    const handleBeforeUnload = (event: BeforeUnloadEvent): void => {
      if (!isAnyActionBlockedFn()) return

      // Trigger the browser's built-in confirmation prompt. Modern browsers show their own generic
      // message; preventDefault + returnValue covers both current and legacy browsers.
      event.preventDefault()
      event.returnValue = ''
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [isAnyActionBlockedFn])

  return null
}

export default UnsavedActionsReloadGuard
